import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertCircle, Package, Clock, Loader2, Sparkles, CheckCircle2, Camera } from 'lucide-react';
import { createAsset, updateAsset, Asset } from '../lib/assets';
import AssetAvailability from './assets/AssetAvailability';
import { decodeVin, isPlausibleVin, vinDecodeSummary } from '../lib/vinDecode';
import { supabase } from '../lib/supabase';

interface AssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
  asset?: Asset | null;
}

const ASSET_TYPES = [
  { value: '', label: 'Select type...' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'tool', label: 'Tool' },
  { value: 'room', label: 'Room / Space' },
  { value: 'other', label: 'Other' },
];

export default function AssetModal({ isOpen, onClose, onSuccess, businessId, asset }: AssetModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!asset;

  const defaultForm = {
    name: '',
    asset_type: '',
    description: '',
    status: 'active',
    not_bookable: false,
    unit_number: '',
    vehicle_year: '',
    vehicle_make: '',
    vehicle_model: '',
    vin: '',
  };

  const [formData, setFormData] = useState(defaultForm);
  const [decoding, setDecoding] = useState(false);
  const [decodeSummary, setDecodeSummary] = useState<string | null>(null);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  // Profile picture — uploaded to the shared avatars bucket, stored on assets.image_url.
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file
    if (!file || !supabase) return;
    try {
      setUploadingPhoto(true);
      setError(null);
      const fileExt = file.name.split('.').pop() || 'jpg';
      // Timestamped name: replacing the photo gets a fresh URL (no stale cache).
      const fileName = `assets/${asset?.id || crypto.randomUUID()}-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(fileName);
      // Existing assets persist immediately (like team member photos) — no
      // "Update Asset" click required. New assets save with Create.
      if (asset?.id) {
        await updateAsset(asset.id, { image_url: publicUrl });
      }
      setImageUrl(publicUrl);
    } catch (err) {
      console.error('Asset photo upload failed:', err);
      setError('Photo upload failed — please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      setError(null);
      if (asset?.id) {
        await updateAsset(asset.id, { image_url: null });
      }
      setImageUrl(null);
    } catch (err) {
      console.error('Asset photo remove failed:', err);
      setError('Failed to remove photo — please try again.');
    }
  };

  const handleDecodeVin = async () => {
    setDecodeError(null);
    setDecodeSummary(null);
    if (!isPlausibleVin(formData.vin)) {
      setDecodeError('Enter the full 17-character VIN first.');
      return;
    }
    setDecoding(true);
    try {
      const d = await decodeVin(formData.vin);
      if (!d.valid) {
        setDecodeError(d.error || 'This VIN could not be decoded.');
        return;
      }
      setFormData(prev => ({
        ...prev,
        vehicle_year: d.year ? String(d.year) : prev.vehicle_year,
        vehicle_make: d.make || prev.vehicle_make,
        vehicle_model: d.model || prev.vehicle_model,
      }));
      setDecodeSummary(vinDecodeSummary(d));
    } catch (err) {
      console.error('VIN decode failed:', err);
      setDecodeError('Decode failed — check your connection and try again.');
    } finally {
      setDecoding(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      if (asset) {
        setFormData({
          name: asset.name || '',
          asset_type: asset.asset_type || '',
          description: asset.description || '',
          status: asset.status || 'active',
          not_bookable: asset.not_bookable ?? false,
          unit_number: asset.unit_number || '',
          vehicle_year: asset.vehicle_year?.toString() || '',
          vehicle_make: asset.vehicle_make || '',
          vehicle_model: asset.vehicle_model || '',
          vin: asset.vin || '',
        });
      } else {
        setFormData(defaultForm);
      }
      setImageUrl(asset?.image_url ?? null);
      setError(null);
    }
  }, [isOpen, asset]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setFormData(defaultForm);
      setError(null);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!formData.name.trim()) {
        throw new Error('Asset name is required');
      }

      const isVehicle = formData.asset_type === 'vehicle';
      const vehicleFields = isVehicle ? {
        unit_number: formData.unit_number.trim() || null,
        vehicle_year: formData.vehicle_year ? parseInt(formData.vehicle_year, 10) : null,
        vehicle_make: formData.vehicle_make.trim() || null,
        vehicle_model: formData.vehicle_model.trim() || null,
        vin: formData.vin.trim() || null,
      } : {
        unit_number: null,
        vehicle_year: null,
        vehicle_make: null,
        vehicle_model: null,
        vin: null,
      };

      if (isEditing && asset) {
        await updateAsset(asset.id, {
          name: formData.name.trim(),
          asset_type: formData.asset_type || null,
          description: formData.description.trim() || null,
          status: formData.status,
          not_bookable: formData.not_bookable,
          image_url: imageUrl,
          ...vehicleFields,
        });
      } else {
        await createAsset({
          business_id: businessId,
          name: formData.name.trim(),
          asset_type: formData.asset_type || undefined,
          description: formData.description.trim() || undefined,
          status: formData.status,
          not_bookable: formData.not_bookable,
          image_url: imageUrl || undefined,
          ...vehicleFields as any,
        });
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving asset:', err);
      setError(err instanceof Error ? err.message : 'Failed to save asset');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0',
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[560px] lg:w-[600px] ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050,
          touchAction: 'manipulation',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Package className="h-5 w-5 text-orange-500" />
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              {isEditing ? 'Edit Asset' : 'New Asset'}
            </h2>
          </div>
          <p className="text-sm text-gray-600">
            {isEditing ? 'Update asset details' : 'Add a new asset to your business'}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-white" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
            {error && (
              <div className="bg-red-50 border border-red-200/60 text-red-700 px-4 py-3 rounded-xl text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Asset Information */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Asset Information</h3>
                <div className="space-y-4">
                  {/* Profile picture */}
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="relative h-20 w-20 flex-shrink-0 rounded-2xl overflow-hidden border border-gray-200/60 bg-orange-100 flex items-center justify-center group active:scale-95 transition-all duration-150 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      aria-label="Set asset photo"
                    >
                      {imageUrl ? (
                        <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <Package className="h-8 w-8 text-orange-600" />
                      )}
                      <span className={`absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity ${uploadingPhoto ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        {uploadingPhoto
                          ? <Loader2 className="h-5 w-5 text-white animate-spin" />
                          : <Camera className="h-5 w-5 text-white" />}
                      </span>
                    </button>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">Profile picture</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Shows on the asset list and wherever this asset is assigned.
                      </p>
                      <div className="mt-1.5 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => photoInputRef.current?.click()}
                          disabled={uploadingPhoto}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50"
                        >
                          {imageUrl ? 'Change photo' : 'Upload photo'}
                        </button>
                        {imageUrl && (
                          <button
                            type="button"
                            onClick={handleRemovePhoto}
                            disabled={uploadingPhoto}
                            className="text-xs font-semibold text-red-500 hover:text-red-600 disabled:opacity-50"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Pressure Washer #1, Van A, Crane"
                      className="w-full px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200"
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={formData.asset_type}
                      onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })}
                      className="w-full px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200 appearance-none"
                    >
                      {ASSET_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional notes about this asset"
                      rows={3}
                      className="w-full px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200 resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle Details — only when type is 'vehicle' */}
              {formData.asset_type === 'vehicle' && (
                <div className="bg-white rounded-[12px] border border-blue-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Vehicle Details</h3>
                  <div className="space-y-4">
                    {/* Unit Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit Number</label>
                      <input
                        type="text"
                        value={formData.unit_number}
                        onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                        placeholder="e.g. V-001"
                        className="w-full px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200"
                      />
                    </div>

                    {/* Year / Make / Model */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                        <input
                          type="number"
                          value={formData.vehicle_year}
                          onChange={(e) => setFormData({ ...formData, vehicle_year: e.target.value })}
                          placeholder="2024"
                          min="1900"
                          max="2099"
                          className="w-full px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Make</label>
                        <input
                          type="text"
                          value={formData.vehicle_make}
                          onChange={(e) => setFormData({ ...formData, vehicle_make: e.target.value })}
                          placeholder="Ford"
                          className="w-full px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                        <input
                          type="text"
                          value={formData.vehicle_model}
                          onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
                          placeholder="Transit"
                          className="w-full px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200"
                        />
                      </div>
                    </div>

                    {/* VIN — decode autofills year/make/model */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.vin}
                          onChange={(e) => {
                            setFormData({ ...formData, vin: e.target.value.toUpperCase() });
                            setDecodeSummary(null);
                            setDecodeError(null);
                          }}
                          placeholder="e.g. 1FTBW2CM5JKA12345"
                          maxLength={17}
                          className="flex-1 min-w-0 px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200 font-mono tracking-wider"
                        />
                        <button
                          type="button"
                          onClick={handleDecodeVin}
                          disabled={decoding || !isPlausibleVin(formData.vin)}
                          className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {decoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                          Decode
                        </button>
                      </div>
                      {decodeSummary && (
                        <p className="mt-2 flex items-start gap-1.5 text-sm text-emerald-700">
                          <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>{decodeSummary}</span>
                        </p>
                      )}
                      {decodeError && (
                        <p className="mt-2 text-sm text-red-600">{decodeError}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Settings */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Settings</h3>
                <div className="space-y-4">
                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-3 text-base bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all duration-200 appearance-none"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  {/* Not Bookable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-orange-50/80 backdrop-blur-sm border border-orange-200/50 rounded-2xl">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900">Not Bookable</label>
                      <p className="text-xs text-gray-500 mt-0.5">
                        When enabled, this asset won't appear when assigning assets to bookings
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, not_bookable: !formData.not_bookable })}
                      className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        formData.not_bookable ? 'bg-orange-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          formData.not_bookable ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 text-base font-semibold rounded-2xl transition-all duration-300 ease-out transform active:scale-[0.98] touch-manipulation shadow-lg ${
                  loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/30 hover:shadow-blue-700/40'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </div>
                ) : (
                  isEditing ? 'Update Asset' : 'Create Asset'
                )}
              </button>
            </form>

            {/* Working Schedule — only for existing assets */}
            {isEditing && asset && businessId && (
              <div className="mt-6 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200/60 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <h3 className="text-base font-semibold text-gray-900">Working Schedule</h3>
                </div>
                <AssetAvailability assetId={asset.id} businessId={businessId} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
